import { Suspense, use } from 'react'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Link, useLoaderData } from 'react-router'
import { Flex } from '@radix-ui/themes'
import { PageHeading } from '@/components/subscription/page-heading'
import { Button } from '~/components/subscription/button'
import { TextLink } from '~/components/subscription/text-link'
import { ZendeskWidget } from '@/components/zendesk-widget'
import { InBulkCard } from './in-bulk-card'
import { AddInBulkCard } from './add-in-bulk-card'
import { formatDate, formatTime } from '@/lib/shipment'
import { findSchedule, findShippingStatus } from '@/lib/order'
import { createClient } from '@/api-client'
import { Me } from '@/api-client/Me'
import { Subscriptions } from '@/api-client/Subscriptions'
import { SubscriptionSchedules } from '@/api-client/SubscriptionSchedules'
import { Cards } from '@/api-client/Cards'
import { SubscriptionInventory, FlowerCollectionProduct } from '~/api-client/data-contracts'
import * as styles from './styles.css'
import box from './box.svg'
import chevron from './chevron.svg'

const ChangeLink: React.FC<{ label?: string }> = ({ label = '変更' }) => {
  return (
    <Flex direction="row" align="center" justify="center" gap="4px" className={styles.changeLink}>
      {label}
      <img src={chevron} width="6px" style={{ marginTop: '1px' }} />
    </Flex>
  )
}

export const loader = async () => {
  const account = await fetchMe()
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0]

  let placements = { data: { data: [] } } as Awaited<ReturnType<typeof fetchPlacements>>
  let address = { data: { data: { attributes: {} } } } as Awaited<ReturnType<typeof fetchAddress>>
  let schedules = { data: { data: [] } } as Awaited<ReturnType<typeof fetchSchedules>>
  let product = { data: { data: null } } as Awaited<ReturnType<typeof fetchProduct>>
  let inBulkPurchasables = { data: { data: [] } } as Awaited<ReturnType<typeof fetchInBulkPurchasables>>
  let orders = { data: { data: [] } } as Awaited<ReturnType<typeof fetchOrders>>
  let recentDeliveries = { data: { data: [] } } as Awaited<ReturnType<typeof fetchRecentDeliveries>>
  if (subscription) {
    ;[placements, address, product, inBulkPurchasables, orders, recentDeliveries] = await Promise.all([
      fetchPlacements(subscription.id),
      fetchAddress(subscription.id),
      fetchProduct(subscription.id),
      fetchInBulkPurchasables(),
      fetchOrders(subscription.id),
      fetchRecentDeliveries(),
    ])
    schedules = await fetchSchedules(address.data.data.attributes.postal_code)
  } else {
    recentDeliveries = await fetchRecentDeliveries()
  }

  return [account, subscriptions, placements, address, schedules, product, inBulkPurchasables, orders, recentDeliveries] as const
}

function fetchMe() {
  return createClient(Me).getMe()
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions({ category: 'lossless_bouquet' })
}

function fetchPlacements(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdPlacements(id)
}

function fetchAddress(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(id)
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, category: 'lossless_bouquet', upcoming: '1', limit: '9' })
}

function fetchProduct(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdProduct(id)
}

function fetchInBulkPurchasables() {
  return createClient(Subscriptions).getSubscriptionsInBulkPurchasables({ category: 'lossless_bouquet' })
}

function fetchOrders(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdOrders(id, { include: ['order_items.shipping_statuses'] })
}

function fetchRecentDeliveries() {
  return createClient(Subscriptions).getSubscriptionsPlacementsOrdered({ category: 'lossless_bouquet', include: ['schedule'], per_page: '5' })
}

function fetchDefaultCard() {
  return createClient(Cards).getCardsDefault()
}

const creditCardPromise = fetchDefaultCard()
  .then((res) => res.data.data)
  .catch(() => null)

export const SubscriptionMypageTop: React.FC = () => {
  const [
    {
      data: { data: account },
    },
    {
      data: { data: subscriptions, included },
    },
    {
      data: { data: placements },
    },
    {
      data: { data: address },
    },
    {
      data: { data: schedules },
    },
    {
      data: { data: product },
    },
    {
      data: { data: inBulkPurchasables },
    },
    {
      data: { data: orders, included: ordersIncluded },
    },
    {
      data: { data: recentDeliveries, included: recentDeliveriesIncluded },
    },
  ] = useLoaderData<typeof loader>()
  const creditCard = use(creditCardPromise)

  const subscription = subscriptions[0]
  const orderableName = product?.attributes.name
  const inBulkProducts = included.filter((item) => {
    return item.type === 'flower_collection_product' && item.attributes.bulk_discounted_price > 0
  }) as unknown as FlowerCollectionProduct[]
  const date = subscription && new Date(subscription.attributes.next_deliver_at * 1000)
  const weekday = date ? format(date, 'E', { locale: ja }) : ''
  const currentSchedules =
    subscription?.relationships.inventories.data
      .map((inventory: SubscriptionInventory) => schedules.find((s) => s.relationships.inventory.data.id === inventory.id))
      .sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0]) || []
  const latestShipping = findShippingStatus(orders[0], ordersIncluded)
  const latestShippingStatus = latestShipping?.attributes?.status
  const nextPlacementStatus = placements[0]?.attributes?.status
  const recentDeliveryImages = recentDeliveries
    .map((placement) => ({ id: placement.id, url: findSchedule(placement, recentDeliveriesIncluded ?? [])?.attributes.image_url }))
    .filter((image): image is { id: string; url: string } => !!image.url)

  return (
    <Flex direction="column" align="center" gap="32px">
      <PageHeading>マイページ</PageHeading>
      {subscription ? (
        <>
          <p className={styles.note}>{formatTime(1000 * subscription.attributes.skip_deadline)}まで今後のお届け分のスキップ・配送先などの変更が可能です</p>
          {['not_shipped', 'preparation', 'transit'].includes(latestShippingStatus) && (
            <Flex direction="column" align="start" gap="8px" className={styles.card}>
              <h3 className={styles.cardHeading}>次回の到着日</h3>
              <Flex direction="row" align="center" gap="4px">
                <p className={styles.date}>{formatDate(1000 * latestShipping.attributes.deliver_at, 'M/d（E）')}</p>
                <span className={styles.statusLabel}>{latestShipping.attributes.status_label}</span>
              </Flex>
              {latestShipping.attributes.tracking_code_url && (
                <Button as="a" href={latestShipping.attributes.tracking_code_url} size="s">
                  追跡する
                </Button>
              )}
            </Flex>
          )}
          <Flex direction="column" align="start" gap="8px" className={styles.card}>
            <h3 className={styles.cardHeading}>今後のお届け予定</h3>
            <Flex direction="column" align="start" gap="4px">
              {currentSchedules.map((schedule, index) => {
                const date = formatDate(schedule?.attributes.deliver_at[0] * 1000, 'M/d（E）')

                return (
                  <Flex key={schedule.id} direction="row" align="center" gap="8px">
                    {index === 0 ? (
                      <span className={styles.nextDate} style={{ textDecoration: nextPlacementStatus === 'skipped' ? 'line-through' : 'none' }}>
                        {date}
                      </span>
                    ) : (
                      <span className={styles.date}>{date}</span>
                    )}
                  </Flex>
                )
              })}
            </Flex>
            <Flex direction="column" align="start" gap="3px">
              <Flex direction="row" align="center" gap="4px" className={styles.note}>
                <img src={box} />
                クロネコヤマトで配送（対面でのお受け取り）
              </Flex>
            </Flex>
            <Flex direction="row" align="center" gap="8px">
              {subscriptions[0].relationships.inventories.data.length === 1 &&
                <Button as="a" href="/subscription/mypage/next" size="s">
                  お届け日の変更
                </Button>}
              {nextPlacementStatus === 'skipped' && (
                <Button as="a" href="/subscription/mypage/skip" size="s" tone="secondary">
                  スキップを取り消す
                </Button>
              )}
              {nextPlacementStatus === 'pending' && (
                <Button as="a" href="/subscription/mypage/skip" size="s" tone="secondary">
                  スキップ
                </Button>
              )}
            </Flex>
          </Flex>
        </>
      ) : (
        <Flex direction="column" align="start" gap="8px" className={styles.card}>
          <p className={styles.text}>ご契約中の定期便はありません</p>
          <Button as="a" href="/subscription/new" size="s">
            定期便を申し込む
          </Button>
        </Flex>
      )}
      <InBulkCard subscription={subscription} product={product} inBulkProducts={inBulkProducts} placements={placements} />
      <AddInBulkCard subscription={subscription} nextBulkProducts={inBulkProducts} inBulkProducts={inBulkPurchasables} />
      {subscription && (
        <a href="/subscription/mypage/schedule" className={styles.card}>
          <h3 className={styles.cardHeading}>お届けスケジュール</h3>
          <Flex direction="row" align="start" justify="between" gap="12px" width="100%">
            <p className={styles.text}>月に{currentSchedules.length}回・{weekday}曜日にお届け</p>
            <ChangeLink />
          </Flex>
        </a>
      )}
      {recentDeliveryImages.length > 0 && (
        <a href="/subscription/mypage/orders" className={styles.card}>
          <h3 className={styles.cardHeading}>お届け履歴</h3>
          <Flex direction="row" align="start" justify="between" gap="12px" width="100%">
            <Flex direction="row" gap="8px" style={{ flex: 1 }}>
              {recentDeliveryImages.map((image) => (
                <img key={image.id} src={image.url} className={styles.recentDeliveryImage} />
              ))}
            </Flex>
            <ChangeLink label="見る" />
          </Flex>
        </a>
      )}
      {subscription && (
        <a href="/subscription/mypage/address" className={styles.card}>
          <h3 className={styles.cardHeading}>お届け先</h3>
          <Flex direction="row" align="start" justify="between" gap="12px" width="100%">
            {address?.attributes ? (
              <Flex direction="column" gap="2px" className={styles.text}>
                <span>〒{address.attributes.postal_code}</span>
                <span>
                  {address.attributes.state}
                  {address.attributes.city_and_street}
                </span>
                <span>{address.attributes.others}</span>
                <span>
                  {address.attributes.name}
                  {!!address.attributes.sender_name ? `（${address.attributes.sender_name}より）` : ''}
                </span>
              </Flex>
            ) : (
              <p className={styles.warn}>登録されていません</p>
            )}
            <ChangeLink />
          </Flex>
        </a>
      )}
      <a href="/subscription/mypage/card" className={styles.card}>
        <h3 className={styles.cardHeading}>クレジットカード</h3>
        <Flex direction="row" align="start" justify="between" gap="12px" width="100%">
          <Suspense fallback={<></>}>
            {creditCard ? <p className={styles.text}>**** **** **** {creditCard.attributes?.last4}</p> : <p className={styles.warn}>登録されていません</p>}
          </Suspense>
          <ChangeLink />
        </Flex>
      </a>
      <a href="/subscription/mypage/email" className={styles.card}>
        <h3 className={styles.cardHeading}>メールアドレス</h3>
        <Flex direction="row" align="start" justify="between" gap="12px" width="100%">
          <Flex direction="column" gap="2px" className={styles.text}>
            <span>{account.attributes.email}</span>
          </Flex>
          <ChangeLink />
        </Flex>
      </a>
      <a href="/subscription/mypage/password" className={styles.card}>
        <h3 className={styles.cardHeading}>パスワード</h3>
        <Flex direction="row" align="start" justify="between" gap="12px" width="100%">
          <Flex direction="column" gap="2px" className={styles.text}>
            <span>****</span>
          </Flex>
          <ChangeLink />
        </Flex>
      </a>
      <Flex direction="column" align="center" gap="24px">
        {subscription && <TextLink to="/subscription/mypage/cancel">定期便の解約</TextLink>}
        <TextLink to="/subscription/mypage/delete">アカウントの削除</TextLink>
      </Flex>
      <ZendeskWidget orderableName={orderableName} userId={account?.id} userEmail={account?.attributes.email} />
    </Flex>
  )
}
